import {
    GenericTableColumn,
    GenericTableColumnType,
    GenericTableDateColumnFormat,
    GenericTableNumberFormat,
} from 'inside-fifa/api/generic-tables/dto';
import { Fragment, useCallback, useEffect, useState } from 'react';
import {
    Button,
    CardContent,
    Checkbox,
    Dialog,
    DialogActions,
    DialogTitle,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    TextField,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { LANGUAGE_OPTIONS } from 'inside-fifa/utils/i18n';
import { getDefaultSettings } from './utils';

type FormatSettingsDialogProps = {
    open: boolean;
    column: GenericTableColumn;
    languages: string[];
    onSave: (newColumn: GenericTableColumn) => void;
    onCancel: () => void;
};

export const FormatSettingsDialog = ({ open, column, languages, onSave, onCancel }: FormatSettingsDialogProps) => {
    const [columnState, setColumnState] = useState(column);

    useEffect(() => {
        setColumnState((prevColumn) => ({
            ...column,
            numberFormat:
                column.numberFormat === undefined ||
                column.numberFormat === GenericTableNumberFormat.Unknown
                    ? GenericTableNumberFormat.Raw
                    : column.numberFormat,
        }));
    }, [column]);

    const onCancelInternal = useCallback(() => {
        // Reset state
        setColumnState(column);
        onCancel();
    }, [column, onCancel]);

    // Settings that are uncovered on specific types of the formatting
    const columnExtraSettings = (() => {
        switch (columnState.type) {
            case GenericTableColumnType.Number:
                
                return (
                    <>
                        <Grid item>
                            <InputLabel>
                                <FormattedMessage id="inside-fifa.generic-tables.number-format" />
                            </InputLabel>
                        </Grid>
                        <Grid item>
                            <Select<GenericTableNumberFormat>
                                fullWidth
                                value={columnState.numberFormat ?? GenericTableNumberFormat.Raw}
                                defaultValue={GenericTableNumberFormat.Raw}
                                onChange={(event) => {
                                    const newFormat = event.target.value as GenericTableNumberFormat;
                                    setColumnState((column) => ({ ...column, numberFormat: newFormat }));
                                }}
                            >
                                {columnState.numberFormat === GenericTableNumberFormat.Unknown && (
                                    <MenuItem value={GenericTableNumberFormat.Unknown}>
                                        <FormattedMessage id="inside-fifa.generic-tables.number-format.unknown" />
                                    </MenuItem>
                                )}
                                <MenuItem value={GenericTableNumberFormat.Raw}>
                                    <FormattedMessage id="inside-fifa.generic-tables.number-format.raw" />
                                </MenuItem>
                                <MenuItem value={GenericTableNumberFormat.ShortNumber}>
                                    <FormattedMessage id="inside-fifa.generic-tables.number-format.short-number" />
                                </MenuItem>
                                <MenuItem value={GenericTableNumberFormat.USD}>
                                    <FormattedMessage id="inside-fifa.generic-tables.number-format.usd" />
                                </MenuItem>
                                <MenuItem value={GenericTableNumberFormat.ShortUSD}>
                                    <FormattedMessage id="inside-fifa.generic-tables.number-format.short-usd" />
                                </MenuItem>
                                <MenuItem value={GenericTableNumberFormat.Percentage}>
                                    <FormattedMessage id="inside-fifa.generic-tables.number-format.percentage" />
                                </MenuItem>
                            </Select>
                        </Grid>
                    </>
                );
            case GenericTableColumnType.Text:
                return (
                    <>
                        <Grid item>
                            <InputLabel>
                                <FormattedMessage id="inside-fifa.generic-tables.custom-template" />
                            </InputLabel>
                        </Grid>
                        <Grid item>
                            <TextField
                                value={columnState.customTemplateString ?? ''}
                                fullWidth
                                onChange={(event) => {
                                    const newValue = event.currentTarget.value;

                                    setColumnState((column) => ({
                                        ...column,
                                        customTemplateString: newValue !== '' ? newValue : null,
                                    }));
                                }}
                            />
                        </Grid>
                    </>
                );

            case GenericTableColumnType.Boolean: {
                const customizedLabel = Boolean(columnState.trueLabel || columnState.falseLabel);

                return (
                    <>
                        <Grid item>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={customizedLabel}
                                        onChange={(event, checked) => {
                                            if (checked) {
                                                const emptyTranslatableValue = Object.fromEntries(
                                                    languages.map((langCode) => [langCode, '']),
                                                );

                                                setColumnState((column) => ({
                                                    ...column,
                                                    trueLabel: column.trueLabel ?? emptyTranslatableValue,
                                                    falseLabel: column.falseLabel ?? emptyTranslatableValue,
                                                }));
                                                return;
                                            }

                                            setColumnState((column) => ({
                                                ...column,
                                                trueLabel: null,
                                                falseLabel: null,
                                            }));
                                        }}
                                    />
                                }
                                label={<FormattedMessage id="inside-fifa.generic-tables.customize-boolean-label" />}
                            />
                        </Grid>

                        {customizedLabel &&
                            languages.map((langCode, index) => {
                                return (
                                    <Fragment key={`true-${index}`}>
                                        <Grid item>
                                            <InputLabel>
                                                <FormattedMessage
                                                    id="inside-fifa.generic-tables.true-label"
                                                    values={{
                                                        language: (
                                                            <FormattedMessage
                                                                id={
                                                                    LANGUAGE_OPTIONS.find(
                                                                        ({ code }) => code === langCode,
                                                                    )!.translationKey
                                                                }
                                                            />
                                                        ),
                                                    }}
                                                />
                                            </InputLabel>
                                        </Grid>
                                        <Grid item>
                                            <TextField
                                                value={columnState.trueLabel?.[langCode] ?? ''}
                                                fullWidth
                                                onChange={(event) => {
                                                    const newValue = event.currentTarget.value;

                                                    setColumnState((column) => ({
                                                        ...column,
                                                        trueLabel: {
                                                            ...column.trueLabel,
                                                            [langCode]: newValue,
                                                        },
                                                    }));
                                                }}
                                            />
                                        </Grid>
                                    </Fragment>
                                );
                            })}

                        {customizedLabel &&
                            languages.map((langCode, index) => {
                                return (
                                    <Fragment key={`false-${index}`}>
                                        <Grid item>
                                            <InputLabel>
                                                <FormattedMessage
                                                    id="inside-fifa.generic-tables.false-label"
                                                    values={{
                                                        language: (
                                                            <FormattedMessage
                                                                id={
                                                                    LANGUAGE_OPTIONS.find(
                                                                        ({ code }) => code === langCode,
                                                                    )!.translationKey
                                                                }
                                                            />
                                                        ),
                                                    }}
                                                />
                                            </InputLabel>
                                        </Grid>
                                        <Grid item>
                                            <TextField
                                                value={columnState.falseLabel?.[langCode] ?? ''}
                                                fullWidth
                                                onChange={(event) => {
                                                    const newValue = event.currentTarget.value;
                                                    setColumnState((column) => ({
                                                        ...column,
                                                        falseLabel: {
                                                            ...column.falseLabel,
                                                            [langCode]: newValue,
                                                        },
                                                    }));
                                                }}
                                            />
                                        </Grid>
                                    </Fragment>
                                );
                            })}
                    </>
                );
            }
            case GenericTableColumnType.Date:
                return (
                    <>
                        <Grid item>
                            <InputLabel>
                                <FormattedMessage id="inside-fifa.generic-tables.date-format" />
                            </InputLabel>
                        </Grid>
                        <Grid item>
                            <Select<GenericTableDateColumnFormat>
                                fullWidth
                                value={columnState.dateFormat ?? GenericTableDateColumnFormat.FullDate}
                                onChange={(event) => {
                                    const newFormat = event.target.value as GenericTableDateColumnFormat;

                                    setColumnState((column) => ({ ...column, dateFormat: newFormat }));
                                }}
                            >
                                {columnState.dateFormat === GenericTableDateColumnFormat.Unknown && (
                                    <MenuItem value={GenericTableDateColumnFormat.Unknown}>
                                        <FormattedMessage id="inside-fifa.generic-tables.date-format.unknown" />
                                    </MenuItem>
                                )}
                                <MenuItem value={GenericTableDateColumnFormat.FullDate}>
                                    <FormattedMessage id="inside-fifa.generic-tables.date-format.full-date" />
                                </MenuItem>
                                <MenuItem value={GenericTableDateColumnFormat.WithoutYear}>
                                    <FormattedMessage id="inside-fifa.generic-tables.date-format.without-year" />
                                </MenuItem>
                                <MenuItem value={GenericTableDateColumnFormat.YearOnly}>
                                    <FormattedMessage id="inside-fifa.generic-tables.date-format.year-only" />
                                </MenuItem>
                            </Select>
                        </Grid>
                    </>
                );
            case GenericTableColumnType.FdcpAssociation:
            case GenericTableColumnType.FdcpConfederation:
                const isLogoVisible = Boolean(columnState.isLogoVisible ?? true);

                return (
                    <Grid item>
                        <FormControlLabel
                            defaultChecked
                            control={
                                <Checkbox
                                    checked={isLogoVisible}
                                    onChange={(_event, checked) => {
                                        setColumnState((column) => ({
                                            ...column,
                                            isLogoVisible: checked,
                                        }));
                                    }}
                                />
                            }
                            label={<FormattedMessage id="inside-fifa.generic-tables.display-logo-label" />}
                        />
                    </Grid>
                );
            default:
                return <></>;
        }
    })();

    return (
        <Dialog open={open}>
            <DialogTitle>
                <FormattedMessage
                    id="inside-fifa.generic-tables.column-format-modal-title"
                    values={{ columnKey: column.key }}
                />
            </DialogTitle>
            <CardContent>
                <Grid container direction="column" spacing={1}>
                    <Grid item>
                        <InputLabel>
                            <FormattedMessage id="inside-fifa.generic-tables.column-type" />
                        </InputLabel>
                    </Grid>
                    <Grid item>
                        <Select<GenericTableColumnType>
                            fullWidth
                            value={columnState.type}
                            onChange={(event) => {
                                const newType = event.target.value as GenericTableColumnType;
                                const defaultSettings = getDefaultSettings(newType);

                                setColumnState((column) => ({ ...column, type: newType, ...defaultSettings }));
                            }}
                        >
                            <MenuItem value={GenericTableColumnType.Text}>
                                <FormattedMessage id="inside-fifa.generic-tables.column-format.text" />
                            </MenuItem>
                            <MenuItem value={GenericTableColumnType.Number}>
                                <FormattedMessage id="inside-fifa.generic-tables.column-format.number" />
                            </MenuItem>
                            <MenuItem value={GenericTableColumnType.Boolean}>
                                <FormattedMessage id="inside-fifa.generic-tables.column-format.boolean" />
                            </MenuItem>
                            <MenuItem value={GenericTableColumnType.Date}>
                                <FormattedMessage id="inside-fifa.generic-tables.column-format.date" />
                            </MenuItem>
                            <MenuItem value={GenericTableColumnType.FdcpAssociation}>
                                <FormattedMessage id="inside-fifa.generic-tables.column-format.fdcp-association" />
                            </MenuItem>
                            <MenuItem value={GenericTableColumnType.FdcpConfederation}>
                                <FormattedMessage id="inside-fifa.generic-tables.column-format.fdcp-confederation" />
                            </MenuItem>
                            <MenuItem value={GenericTableColumnType.Url}>
                                <FormattedMessage id="inside-fifa.generic-tables.column-format.url" />
                            </MenuItem>
                            <MenuItem value={GenericTableColumnType.Email}>
                                <FormattedMessage id="inside-fifa.generic-tables.column-format.email" />
                            </MenuItem>
                            <MenuItem value={GenericTableColumnType.BynderId}>
                                <FormattedMessage id="inside-fifa.generic-tables.column-format.bynder-id" />
                            </MenuItem>
                        </Select>
                    </Grid>
                    {columnExtraSettings}
                </Grid>
            </CardContent>
            <DialogActions>
                <Button autoFocus onClick={onCancelInternal} color="error">
                    <FormattedMessage id="inside-fifa.cancel" />
                </Button>
                <Button autoFocus onClick={() => {
                    onSave(columnState)
                }} color="primary" variant="contained">
                    <FormattedMessage id="inside-fifa.save" />
                </Button>
            </DialogActions>
        </Dialog>
    );
};
